if (!customElements.get('lsg-number-stats')) {
  class LsgNumberStats extends HTMLElement {
    constructor() {
      super();
      this.classes = {
        numberElements: 'h3.font-freight',
      };
      this.animationFrameId = null;
      this.targets = [];
      this.startTime = null;
      this.duration = 0;
      this.intersectionObserver = null;
      this.animationStarted = false;
    }

    connectedCallback() {
      const animateRaw = (this.getAttribute('data-animate-numbers') || '').toLowerCase();
      const animateNumbers = animateRaw === 'true' || animateRaw === '1';
      if (!animateNumbers) {
        return;
      }

      const runSetup = () => {
        this.targets = this.extractTargets();
        if (this.targets.length === 0) {
          return;
        }

        this.targets.forEach(({ element, prefix, suffix }) => {
          element.textContent = prefix + (0).toLocaleString() + suffix;
        });

        const startWhenVisible = () => {
          if (this.animationStarted) return;
          this.animationStarted = true;
          this.intersectionObserver?.disconnect();
          this.intersectionObserver = null;
          this.startAnimation();
        };

        if (typeof IntersectionObserver !== 'function') {
          startWhenVisible();
          return;
        }

        const observeTarget =
          this.querySelector('.container-standard') || this.closest('section') || this;

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting || this.animationStarted) return;
              startWhenVisible();
            });
          },
          { root: null, threshold: 0, rootMargin: '0px 0px -5% 0px' }
        );
        this.intersectionObserver.observe(observeTarget);
      };

      requestAnimationFrame(runSetup);
    }

    disconnectedCallback() {
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = null;
      this.animationStarted = false;

      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    }

    extractTargets() {
      const numberElements = this.querySelectorAll(this.classes.numberElements);
      if (numberElements.length === 0) {
        return [];
      }

      return Array.from(numberElements).map((h3) => {
        const text = h3.textContent.trim();
        const match = text.match(/[\d,]+/);
        if (!match) return null;

        const number = parseFloat(match[0].replace(/,/g, ''));
        return {
          element: h3,
          target: number,
          prefix: text.substring(0, match.index),
          suffix: text.substring(match.index + match[0].length),
          originalText: text,
        };
      }).filter((item) => item !== null);
    }

    startAnimation() {
      const animationDuration = parseFloat(this.dataset.animationDuration) || 3;
      this.startTime = performance.now();
      this.duration = animationDuration * 1000;
      this.animate();
    }

    animate() {
      const currentTime = performance.now();
      const elapsed = currentTime - this.startTime;
      const progress = Math.min(elapsed / this.duration, 1);

      const easedProgress = 1 - Math.pow(1 - progress, 3);
      this.updateNumberValues(easedProgress);

      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(() => this.animate());
      } else {
        this.finalizeNumbers();
      }
    }

    updateNumberValues(progress) {
      this.targets.forEach(({ element, target, prefix, suffix }) => {
        const currentValue = Math.floor(target * progress);
        const formattedValue = currentValue.toLocaleString();
        element.textContent = prefix + formattedValue + suffix;
      });
    }

    finalizeNumbers() {
      this.targets.forEach(({ element, target, prefix, suffix }) => {
        const formattedValue = target.toLocaleString();
        element.textContent = prefix + formattedValue + suffix;
      });
    }
  }

  customElements.define('lsg-number-stats', LsgNumberStats);
}